
import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Invoice } from '@/components/InvoiceList';

interface PaymentOptionProps {
  type: 'bank' | 'card';
  isSelected: boolean;
  invoice: Invoice | null;
  onSelect: (type: 'bank' | 'card') => void;
  onSubmit: () => void;
  formatCurrency: (value: number) => string;
  showProcessingFee: (type: 'bank' | 'card') => boolean;
  getProcessingFee: (amount: number, type: 'bank' | 'card') => number;
  chargeAchFees: boolean;
}

const PaymentOption: React.FC<PaymentOptionProps> = ({
  type,
  isSelected,
  invoice,
  onSelect,
  onSubmit,
  formatCurrency,
  showProcessingFee,
  getProcessingFee,
  chargeAchFees
}) => {
  const isBank = type === 'bank';
  const label = isBank ? 'Bank Transfer (ACH)' : 'Credit Card';
  
  // Conditionally show fee text based on logic
  const shouldShowFee = showProcessingFee(type);
  const feeAmount = shouldShowFee && invoice ? getProcessingFee(invoice.amount, type) : 0;
  const description = shouldShowFee 
    ? `${formatCurrency(feeAmount)} processing fee` 
    : 'No additional fees';

  console.log(`[DEBUG][PaymentOption] Payment method: ${type}, showFee: ${shouldShowFee}, feeAmount: ${feeAmount}, chargeAchFees: ${chargeAchFees}`);

  return (
    <div className="mb-4">
      <div
        className={`border rounded-lg transition-all duration-200 ${
          isSelected ? 'border-keepmore-purple shadow-md' : 'border-gray-200'
        }`}
      >
        <div 
          className="p-4 cursor-pointer flex items-center justify-between"
          onClick={() => onSelect(type)}
        >
          <div className="flex items-center">
            <div className={`w-5 h-5 rounded-full border ${isSelected ? 'border-4 border-keepmore-purple' : 'border border-gray-300'}`}></div>
            <div className="ml-3">
              <h3 className="font-medium">{label}</h3>
              <p className="text-sm text-gray-500">{description}</p>
            </div>
          </div>
        </div>

        {isSelected && (
          <div className="border-t border-gray-100 px-4 py-5">
            {isBank ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="account-name">Account Name</Label>
                    <Input id="account-name" className="mt-1" placeholder="Enter account name" />
                  </div>
                  <div>
                    <Label htmlFor="account-number">Account Number</Label>
                    <Input id="account-number" className="mt-1" placeholder="Enter account number" />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="routing-number">Routing Number</Label>
                    <Input id="routing-number" className="mt-1" placeholder="Enter routing number" />
                  </div>
                </div>
                
                <div className="space-y-2 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>{invoice ? formatCurrency(invoice.amount) : '$0.00'}</span>
                  </div>
                  
                  {/* Only show ACH processing fee if enabled */}
                  {isBank && showProcessingFee('bank') && invoice && (
                    <div className="flex items-center justify-between text-amber-700">
                      <span>Processing fee (ACH):</span>
                      <span>{formatCurrency(getProcessingFee(invoice.amount, 'bank'))}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between font-bold border-t border-gray-200 pt-2 mt-2">
                    <span>Total:</span>
                    <span>
                      {invoice ? 
                        formatCurrency(invoice.amount + (showProcessingFee('bank') ? getProcessingFee(invoice.amount, 'bank') : 0))
                        : '$0.00'}
                    </span>
                  </div>
                </div>
                
                <Button onClick={onSubmit} className="w-full gradient-bg">
                  Complete Payment
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="card-name">Cardholder Name</Label>
                    <Input id="card-name" className="mt-1" placeholder="Name on card" />
                  </div>
                  <div>
                    <Label htmlFor="card-number">Card Number</Label>
                    <Input id="card-number" className="mt-1" placeholder="1234 5678 9012 3456" />
                  </div>
                  <div>
                    <Label htmlFor="expiry-date">Expiry Date</Label>
                    <Input id="expiry-date" className="mt-1" placeholder="MM/YY" />
                  </div>
                  <div>
                    <Label htmlFor="cvc">CVC</Label>
                    <Input id="cvc" className="mt-1" placeholder="123" />
                  </div>
                </div>
                
                <div className="space-y-2 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>{invoice ? formatCurrency(invoice.amount) : '$0.00'}</span>
                  </div>
                  
                  {/* Always show card processing fee */}
                  <div className="flex items-center justify-between text-amber-700">
                    <span>Processing fee (Card):</span>
                    <span>{invoice ? formatCurrency(getProcessingFee(invoice.amount, 'card')) : '$0.00'}</span>
                  </div>
                  
                  <div className="flex items-center justify-between font-bold border-t border-gray-200 pt-2 mt-2">
                    <span>Total:</span>
                    <span>{invoice ? formatCurrency(invoice.amount + getProcessingFee(invoice.amount, 'card')) : '$0.00'}</span>
                  </div>
                </div>
                
                <Button onClick={onSubmit} className="w-full gradient-bg">
                  Complete Payment
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentOption;
